import Badge from '@/components/base/Badge';
import { auditImpactTone, auditOutcomeTone, clockOf } from '@/utils/audit';
import type { AuditEvent } from '@/types/portal';
import { CATEGORY_LOOK } from './auditLook';

interface AuditEventRowProps {
  event: AuditEvent;
  onSelect: (event: AuditEvent) => void;
}

const capitalise = (text: string) => (text ? text[0].toUpperCase() + text.slice(1) : text);

/**
 * One action in a person's burst: its category icon, what happened, what changed (first field,
 * inline), and flags for failed, denied, sensitive or critical actions. Opens the full record.
 */
export default function AuditEventRow({ event, onSelect }: AuditEventRowProps) {
  const look = CATEGORY_LOOK[event.category];
  const failed = event.outcome !== 'Success';
  const heavy = event.impact !== 'Standard';
  const change = event.changes[0];

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(event)}
        title={`${event.category} · ${event.timestamp.replace('T', ' ').slice(0, 16)} · open the full record`}
        className={`choice-row group flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-background-100 ${
          failed ? 'bg-primary-50/50' : ''
        }`}
      >
        <span
          className={`choice-icon mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm ${look.node} ${
            failed ? 'ring-2 ring-primary-400' : heavy ? 'audit-heavy ring-2 ring-secondary-300' : ''
          }`}
          aria-label={event.category}
        >
          <i className={look.icon} aria-hidden="true" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm leading-snug text-foreground-800">{capitalise(event.summary)}</span>
          {change ? (
            <span className="mt-1 inline-flex max-w-full flex-wrap items-center gap-1 rounded-md bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600">
              <span className="font-medium text-foreground-700">{change.field}:</span>
              <span className="truncate line-through decoration-primary-300">{change.before || '—'}</span>
              <i className="ri-arrow-right-line text-foreground-400" aria-hidden="true" />
              <span className="truncate font-medium text-foreground-900">{change.after || '—'}</span>
              {event.changes.length > 1 ? <span className="text-foreground-400">+{event.changes.length - 1} more</span> : null}
            </span>
          ) : null}
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-links-line" aria-hidden="true" />
              {event.resource} · {event.resourceId}
            </span>
            {event.market ? <span className="text-foreground-400">{event.market}</span> : null}
          </span>
        </span>

        <span className="flex shrink-0 flex-col items-end gap-1">
          <time className="text-xs font-label tabular-nums text-foreground-400">{clockOf(event.timestamp)}</time>
          {heavy ? (
            <Badge tone={auditImpactTone(event.impact)} icon="ri-alert-line">
              {event.impact}
            </Badge>
          ) : null}
          {failed ? <Badge tone={auditOutcomeTone(event.outcome)}>{event.outcome}</Badge> : null}
        </span>

        <i className="choice-arrow ri-arrow-right-s-line mt-0.5 hidden text-lg text-foreground-300 sm:block" aria-hidden="true" />
      </button>
    </li>
  );
}
